import { DecodeError as IODecodeError } from 'io-ts/lib/Decoder'
import { AxiosError } from 'axios'

interface HttpError {
    readonly _tag: 'HttpError'
    readonly error: AxiosError
}

interface DecodeError {
    readonly _tag: 'DecodeError'
    readonly error: IODecodeError
}

interface Success<T> {
    readonly _tag: 'Success'
    readonly data: T
}

export type ApiResult<T> = Success<T> | HttpError | DecodeError

const success = <T>(data: T): Success<T> => ({ _tag: 'Success', data })
const httpError = (error: AxiosError): HttpError => ({ _tag: 'HttpError', error })
const decodeError = (error: IODecodeError): DecodeError => ({ _tag: 'DecodeError', error })

const isSuccess = <T>(result: ApiResult<T>): result is Success<T> => result._tag === 'Success'
const isFailure = <T>(result: ApiResult<T>): result is HttpError | DecodeError => result._tag !== 'Success'

type CataCases<T, O> = {
    Success: (data: T) => O
    DecodeError: (error: IODecodeError) => O
    HttpError: (error: AxiosError) => O
}

type CataCasesWithDefault<T, O> = Partial<CataCases<T, O>> & { _: () => O }

const cata = <T, O = void>(result: ApiResult<T>, cases: CataCases<T, O> | CataCasesWithDefault<T, O>): O => {
    const orDefault = <F>(fn?: F): F extends undefined ? (result: ApiResult<T>) => O : F => fn || cases['_']

    switch (result._tag) {
        case 'Success':
            return orDefault(cases.Success)(result.data)
        case 'HttpError':
            return orDefault(cases.HttpError)(result.error)
        case 'DecodeError':
            return orDefault(cases.DecodeError)(result.error)
    }
}

const compose = <A, B>(a: ApiResult<A>, b: ApiResult<B>): ApiResult<[A, B]> => {
    if (isSuccess(a) && isSuccess(b)) {
        return success([a.data, b.data])
    } else if (isFailure(a)) {
        return a
    }

    return b as ApiResult<[A, B]>
}

export { success, httpError, decodeError, isSuccess, isFailure, cata, compose }
